import type { Layer, LayersList } from '@deck.gl/core'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useMap } from '@vis.gl/react-google-maps'
import {
  GoogleMapsOverlay,
  type GoogleMapsOverlayProps,
} from '@deck.gl/google-maps'
import {
  aGoogleMapIsPanning,
  aGoogleMapIsWheelZooming,
  aGoogleMapSimplifiedMode,
  useAtomValue,
} from '~/atoms'
import {
  pickGoogleDeckClick,
  pickGoogleDeckObject,
  type GoogleDeckClickResult,
  type GoogleDeckPickResult,
} from './googleDeckPicking'

export type GoogleDeckOverlayProps = GoogleMapsOverlayProps & {
  clickPickRadius?: number
  hoverPickRadius?: number
  onDirectClick?: (result: GoogleDeckClickResult) => void
  onDirectPickChange?: (result: GoogleDeckPickResult) => void
}

type GoogleDeckLayerGroupHandlers = {
  onBackgroundClick?: (result: GoogleDeckClickResult) => void
  onClick?: (result: GoogleDeckClickResult) => void
  onHover?: (result: GoogleDeckPickResult | null) => void
}

export type GoogleDeckLayerGroupOptions = GoogleDeckLayerGroupHandlers & {
  clickPickRadius?: number
  hoverPickRadius?: number
  id: string
  layers: LayersList
  order: number
}

type RegisteredGoogleDeckLayerGroup = {
  clickPickRadius: number
  hasBackgroundClickHandler: boolean
  hasClickHandler: boolean
  hasHoverHandler: boolean
  hoverPickRadius: number
  id: string
  layerIds: Set<string>
  layers: LayersList
  onBackgroundClick: (result: GoogleDeckClickResult) => void
  onClick: (result: GoogleDeckClickResult) => void
  onHover: (result: GoogleDeckPickResult | null) => void
  order: number
  registrationOrder: number
}

type GoogleDeckOverlayContextValue = {
  registerGroup: (group: GoogleDeckLayerGroupOptions) => () => void
}

type GoogleMapCursorOptions = Pick<
  google.maps.MapOptions,
  'draggableCursor' | 'draggingCursor'
>

const DEFAULT_HOVER_PICK_RADIUS = 6
const DEFAULT_CLICK_PICK_RADIUS = 10
const GOOGLE_LISTING_DECK_GROUP_ID = 'google-map-listing-layers'

const GoogleDeckOverlayContext =
  createContext<GoogleDeckOverlayContextValue | null>(null)

type GoogleMapsOverlayInternals = {
  _deck?: {
    redraw?: () => void
  } | null
  _overlay?: {
    draw?: () => void
    requestRedraw?: () => void
  } | null
}

function redrawGoogleDeckOverlay(overlay: GoogleMapsOverlay) {
  const overlayInternals = overlay as unknown as GoogleMapsOverlayInternals

  overlayInternals._overlay?.requestRedraw?.()
  overlayInternals._overlay?.draw?.()
  overlayInternals._deck?.redraw?.()
}

function flattenLayers(layers: LayersList): Layer[] {
  return layers.flatMap((layer) => {
    if (!layer) return []

    return Array.isArray(layer) ? flattenLayers(layer) : [layer]
  })
}

function getLayerIds(layers: LayersList) {
  return new Set(flattenLayers(layers).map((layer) => layer.id))
}

function sortGroups(
  groups: Iterable<RegisteredGoogleDeckLayerGroup>,
  direction: 'ascending' | 'descending' = 'ascending'
) {
  return Array.from(groups).sort((a, b) => {
    if (a.order !== b.order) {
      return direction === 'ascending'
        ? a.order - b.order
        : b.order - a.order
    }

    return direction === 'ascending'
      ? a.registrationOrder - b.registrationOrder
      : b.registrationOrder - a.registrationOrder
  })
}

function getPickedLayerIdCandidates(result: GoogleDeckPickResult) {
  const layerIds: string[] = []
  let layer: Layer | null | undefined = result.layer

  if (result.layerId) {
    layerIds.push(result.layerId)
  }

  while (layer) {
    if (!layerIds.includes(layer.id)) {
      layerIds.push(layer.id)
    }
    layer = layer.parent
  }

  return layerIds
}

function ownsPickedLayer(
  group: RegisteredGoogleDeckLayerGroup,
  result: GoogleDeckPickResult
) {
  const layerIds = getPickedLayerIdCandidates(result)

  return layerIds.some((layerId) =>
    Array.from(group.layerIds).some(
      (groupLayerId) =>
        layerId === groupLayerId || layerId.startsWith(`${groupLayerId}-`)
    )
  )
}

function findOwningGroup(
  groups: Iterable<RegisteredGoogleDeckLayerGroup>,
  result: GoogleDeckPickResult
) {
  return sortGroups(groups, 'descending').find((group) =>
    ownsPickedLayer(group, result)
  )
}

function getMaxPickRadius(
  groups: Iterable<RegisteredGoogleDeckLayerGroup>,
  type: 'click' | 'hover'
) {
  return Array.from(groups).reduce((maxRadius, group) => {
    const radius =
      type === 'click' ? group.clickPickRadius : group.hoverPickRadius

    return Math.max(maxRadius, radius)
  }, type === 'click' ? DEFAULT_CLICK_PICK_RADIUS : DEFAULT_HOVER_PICK_RADIUS)
}

export function GoogleDeckOverlayProvider({
  children,
}: {
  children: ReactNode
}) {
  const map = useMap()
  const isSimplifiedMode = useAtomValue(aGoogleMapSimplifiedMode)
  const isWheelZooming = useAtomValue(aGoogleMapIsWheelZooming)
  const isPanning = useAtomValue(aGoogleMapIsPanning)
  const groupsRef = useRef(new Map<string, RegisteredGoogleDeckLayerGroup>())
  const registrationOrderRef = useRef(0)
  const frameIdRef = useRef<number | null>(null)
  const hoveredGroupIdRef = useRef<string | null>(null)
  const isMountedRef = useRef(false)
  const isFinalizedRef = useRef(false)
  const cursorOptionsRef = useRef<GoogleMapCursorOptions>({})
  const overlayPropsRef = useRef<GoogleMapsOverlayProps>({})
  const [revision, setRevision] = useState(0)
  const overlay = useMemo(
    () => new GoogleMapsOverlay({ interleaved: false }),
    []
  )

  const bumpRevision = useCallback(() => {
    setRevision((revision) => revision + 1)
  }, [])

  const setMapCursor = useCallback(
    (nextCursorOptions: GoogleMapCursorOptions) => {
      if (!map) return

      const cursorOptions = cursorOptionsRef.current
      const cursorIsUnchanged =
        cursorOptions.draggableCursor ===
          nextCursorOptions.draggableCursor &&
        cursorOptions.draggingCursor === nextCursorOptions.draggingCursor

      if (cursorIsUnchanged) return

      cursorOptionsRef.current = nextCursorOptions
      map.setOptions(nextCursorOptions)
    },
    [map]
  )

  const registerGroup = useCallback(
    (group: GoogleDeckLayerGroupOptions) => {
      const previous = groupsRef.current.get(group.id)
      const registrationOrder =
        previous?.registrationOrder ?? registrationOrderRef.current++
      const registeredGroup: RegisteredGoogleDeckLayerGroup = {
        clickPickRadius: group.clickPickRadius ?? DEFAULT_CLICK_PICK_RADIUS,
        hasBackgroundClickHandler: Boolean(group.onBackgroundClick),
        hasClickHandler: Boolean(group.onClick),
        hasHoverHandler: Boolean(group.onHover),
        hoverPickRadius: group.hoverPickRadius ?? DEFAULT_HOVER_PICK_RADIUS,
        id: group.id,
        layerIds: getLayerIds(group.layers),
        layers: group.layers,
        onBackgroundClick: group.onBackgroundClick ?? (() => {}),
        onClick: group.onClick ?? (() => {}),
        onHover: group.onHover ?? (() => {}),
        order: group.order,
        registrationOrder,
      }

      groupsRef.current = new Map(groupsRef.current).set(
        group.id,
        registeredGroup
      )
      bumpRevision()

      return () => {
        if (!groupsRef.current.has(group.id)) return

        groupsRef.current = new Map(groupsRef.current)
        groupsRef.current.delete(group.id)

        if (hoveredGroupIdRef.current === group.id) {
          hoveredGroupIdRef.current = null
        }

        bumpRevision()
      }
    },
    [bumpRevision]
  )

  const orderedGroups = useMemo(
    () => sortGroups(groupsRef.current.values()),
    [revision]
  )
  const useZoomSafeListingPresentation =
    isSimplifiedMode && isWheelZooming && !isPanning
  const hiddenLayerIds = useMemo(() => {
    if (!useZoomSafeListingPresentation) return new Set<string>()

    const listingGroup = groupsRef.current.get(GOOGLE_LISTING_DECK_GROUP_ID)
    return new Set(listingGroup?.layerIds ?? [])
  }, [revision, useZoomSafeListingPresentation])
  const overlayProps = useMemo<GoogleMapsOverlayProps>(
    () => ({
      getTooltip: () => null,
      interleaved: false,
      layerFilter: ({ layer }) => {
        if (!layer) return true

        return !Array.from(hiddenLayerIds).some(
          (hiddenLayerId) =>
            layer.id === hiddenLayerId ||
            layer.id.startsWith(`${hiddenLayerId}-`)
        )
      },
      layers: orderedGroups.flatMap((group) => group.layers),
    }),
    [hiddenLayerIds, orderedGroups]
  )

  const scheduleRedraw = useCallback(() => {
    if (!isMountedRef.current || isFinalizedRef.current) return

    if (frameIdRef.current !== null) {
      cancelAnimationFrame(frameIdRef.current)
    }

    frameIdRef.current = requestAnimationFrame(() => {
      frameIdRef.current = null
      if (!isMountedRef.current || isFinalizedRef.current) return

      redrawGoogleDeckOverlay(overlay)
    })
  }, [overlay])

  useEffect(() => {
    isMountedRef.current = true
    isFinalizedRef.current = false

    return () => {
      isMountedRef.current = false
      isFinalizedRef.current = true

      if (frameIdRef.current !== null) {
        cancelAnimationFrame(frameIdRef.current)
        frameIdRef.current = null
      }

      overlay.finalize()
    }
  }, [overlay])

  useEffect(() => {
    if (isFinalizedRef.current) return

    overlayPropsRef.current = overlayProps
    overlay.setProps(overlayProps)
    scheduleRedraw()
  }, [overlay, overlayProps, scheduleRedraw])

  useEffect(() => {
    if (!map || isFinalizedRef.current) {
      if (!isFinalizedRef.current) {
        overlay.setMap(null)
      }
      return
    }

    let attached = false
    let attachFrameId: number | null = null
    let lastRenderingType = map.getRenderingType()

    const attachOverlay = () => {
      attachFrameId = null
      if (!isMountedRef.current || isFinalizedRef.current) return

      if (attached) {
        overlay.setMap(null)
      }

      overlay.setProps(overlayPropsRef.current)
      overlay.setMap(map)
      attached = true
      scheduleRedraw()
    }

    const scheduleAttachOverlay = () => {
      if (attachFrameId !== null) {
        cancelAnimationFrame(attachFrameId)
      }
      attachFrameId = requestAnimationFrame(attachOverlay)
    }

    const listener = map.addListener('renderingtype_changed', () => {
      const nextRenderingType = map.getRenderingType()

      if (nextRenderingType === lastRenderingType) return

      lastRenderingType = nextRenderingType
      scheduleAttachOverlay()
    })

    if (map.getRenderingType() !== google.maps.RenderingType.UNINITIALIZED) {
      scheduleAttachOverlay()
    }

    return () => {
      listener.remove()
      if (attachFrameId !== null) {
        cancelAnimationFrame(attachFrameId)
      }
      attached = false
      overlay.setMap(null)
    }
  }, [map, overlay, scheduleRedraw])

  useEffect(() => {
    if (!map) return

    const listener = map.addListener(
      'mousemove',
      (event: google.maps.MapMouseEvent) => {
        if (isFinalizedRef.current) return

        const groups = groupsRef.current
        const result = pickGoogleDeckObject(
          overlay,
          event,
          getMaxPickRadius(groups.values(), 'hover')
        )
        const owningGroup =
          result.picked && result.layerId
            ? findOwningGroup(groups.values(), result)
            : undefined
        const previousGroupId = hoveredGroupIdRef.current

        if (previousGroupId && previousGroupId !== owningGroup?.id) {
          groups.get(previousGroupId)?.onHover(null)
        }

        if (owningGroup?.hasHoverHandler) {
          owningGroup.onHover(result)
        }

        hoveredGroupIdRef.current = owningGroup?.id ?? null
        const nextDraggableCursor =
          owningGroup?.hasHoverHandler || owningGroup?.hasClickHandler
            ? 'pointer'
            : 'grab'

        setMapCursor({
          draggableCursor: nextDraggableCursor,
          draggingCursor: 'grabbing',
        })
      }
    )

    return () => {
      listener.remove()
    }
  }, [map, overlay, setMapCursor])

  useEffect(() => {
    if (!map) return

    const listener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        if (isFinalizedRef.current) return

        const groups = groupsRef.current
        const result = pickGoogleDeckClick(
          overlay,
          event,
          getMaxPickRadius(groups.values(), 'click')
        )
        const owningGroup =
          result.picked && result.layerId
            ? findOwningGroup(groups.values(), result)
            : undefined

        sortGroups(groups.values(), 'descending').forEach((group) => {
          if (
            group.id !== owningGroup?.id &&
            group.hasBackgroundClickHandler
          ) {
            group.onBackgroundClick(result)
          }
        })

        if (owningGroup?.hasClickHandler) {
          owningGroup.onClick(result)
          return
        }

        sortGroups(groups.values(), 'descending').forEach((group) => {
          if (
            group.id === owningGroup?.id &&
            group.hasBackgroundClickHandler
          ) {
            group.onBackgroundClick(result)
          }
        })
      }
    )

    return () => {
      listener.remove()
    }
  }, [map, overlay])

  useEffect(() => {
    if (!map) return

    setMapCursor({
      draggableCursor: 'grab',
      draggingCursor: 'grabbing',
    })

    return () => {
      setMapCursor({
        draggableCursor: undefined,
        draggingCursor: undefined,
      })
    }
  }, [map, setMapCursor])

  const value = useMemo(
    () => ({
      registerGroup,
    }),
    [registerGroup]
  )

  return (
    <GoogleDeckOverlayContext.Provider value={value}>
      {children}
    </GoogleDeckOverlayContext.Provider>
  )
}

export function useGoogleDeckLayerGroup({
  clickPickRadius,
  hoverPickRadius,
  id,
  layers,
  onBackgroundClick,
  onClick,
  onHover,
  order,
}: GoogleDeckLayerGroupOptions) {
  const context = useContext(GoogleDeckOverlayContext)
  const optionsRef = useRef<GoogleDeckLayerGroupOptions>({
    clickPickRadius,
    hoverPickRadius,
    id,
    layers,
    onBackgroundClick,
    onClick,
    onHover,
    order,
  })

  useEffect(() => {
    optionsRef.current = {
      clickPickRadius,
      hoverPickRadius,
      id,
      layers,
      onBackgroundClick,
      onClick,
      onHover,
      order,
    }
  })

  useEffect(() => {
    if (!context) {
      throw new Error(
        'useGoogleDeckLayerGroup must be used inside GoogleDeckOverlayProvider'
      )
    }

    return context.registerGroup({
      clickPickRadius,
      hoverPickRadius,
      id,
      layers,
      onBackgroundClick: onBackgroundClick
        ? (result) => optionsRef.current.onBackgroundClick?.(result)
        : undefined,
      onClick: onClick
        ? (result) => optionsRef.current.onClick?.(result)
        : undefined,
      onHover: onHover
        ? (result) => optionsRef.current.onHover?.(result)
        : undefined,
      order,
    })
  }, [
    clickPickRadius,
    context,
    hoverPickRadius,
    id,
    layers,
    Boolean(onBackgroundClick),
    Boolean(onClick),
    Boolean(onHover),
    order,
  ])
}

export function GoogleDeckOverlay({
  clickPickRadius = DEFAULT_CLICK_PICK_RADIUS,
  hoverPickRadius = DEFAULT_HOVER_PICK_RADIUS,
  onDirectClick,
  onDirectPickChange,
  ...overlayProps
}: GoogleDeckOverlayProps) {
  const map = useMap()
  const overlayPropsRef = useRef<GoogleMapsOverlayProps>({})
  const overlay = useMemo(
    () => new GoogleMapsOverlay({ interleaved: false }),
    []
  )

  useEffect(() => {
    overlayPropsRef.current = {
      // GoogleMapsOverlay hides all layers on remove; explicitly restore or
      // clear layerFilter when reattaching the same deck instance.
      layerFilter: overlayProps.layerFilter,
      ...overlayProps,
      interleaved: false,
    }
    overlay.setProps(overlayPropsRef.current)
    const frameId = requestAnimationFrame(() =>
      redrawGoogleDeckOverlay(overlay)
    )

    return () => {
      cancelAnimationFrame(frameId)
    }
  }, [overlay, overlayProps])

  useEffect(() => {
    return () => {
      overlay.finalize()
    }
  }, [overlay])

  useEffect(() => {
    if (!map) {
      overlay.setMap(null)
      return
    }

    let attached = false
    let frameId: number | null = null
    let lastRenderingType = map.getRenderingType()

    const attachOverlay = () => {
      if (attached) {
        overlay.setMap(null)
      }
      overlay.setProps(overlayPropsRef.current)
      overlay.setMap(map)
      attached = true
      requestAnimationFrame(() => redrawGoogleDeckOverlay(overlay))
      frameId = null
    }

    const scheduleAttachOverlay = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId)
      }
      frameId = requestAnimationFrame(attachOverlay)
    }

    const listener = map.addListener('renderingtype_changed', () => {
      const nextRenderingType = map.getRenderingType()

      if (nextRenderingType === lastRenderingType) return

      lastRenderingType = nextRenderingType
      scheduleAttachOverlay()
    })

    if (map.getRenderingType() !== google.maps.RenderingType.UNINITIALIZED) {
      scheduleAttachOverlay()
    }

    return () => {
      listener.remove()
      if (frameId !== null) {
        cancelAnimationFrame(frameId)
      }
      attached = false
      overlay.setMap(null)
    }
  }, [map, overlay])

  useEffect(() => {
    if (!map || !onDirectPickChange) return

    const listener = map.addListener(
      'mousemove',
      (event: google.maps.MapMouseEvent) => {
        onDirectPickChange(
          pickGoogleDeckObject(overlay, event, hoverPickRadius)
        )
      }
    )

    return () => {
      listener.remove()
    }
  }, [hoverPickRadius, map, onDirectPickChange, overlay])

  useEffect(() => {
    if (!map || !onDirectClick) return

    const listener = map.addListener(
      'click',
      (event: google.maps.MapMouseEvent) => {
        onDirectClick(pickGoogleDeckClick(overlay, event, clickPickRadius))
      }
    )

    return () => {
      listener.remove()
    }
  }, [clickPickRadius, map, onDirectClick, overlay])

  return null
}
